import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type {
  LifestyleExposure,
  LifestyleExposureFrequency,
  LifestyleExposureType,
} from "@louise/medical-history/basic-info/types";
import { useSuspensePatient } from "@louise/patients/hooks/usePatient";
import { withMedicalHistoryRecordFixture } from "@src/packages/medical-history/test-fixtures/medicalHistoryRecordFixture";
import { useQueuedPatientUpdate } from "@louise/patients/sync/usePatientSync";
import { Patient } from "../../basic-info/calculateBasicInfoCompletion";
import { buildExposureNavigationStack } from "./buildExposureNavigationStack";
import { CommonActions, useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { transformExposureStateToRequest } from "./transformExposureStateToRequest";

export type ExposureType = LifestyleExposureType;
export type ExposureFrequency = LifestyleExposureFrequency;

export type ExposureStackParamList = {
  ExposureTypes: undefined;
  ExposureDetails: { exposure: ExposureType };
  ExposureComplete: undefined;
};

export type ExposureFlowRoute = keyof ExposureStackParamList;

export interface ExposureNavigationEntry<
  Route extends ExposureFlowRoute = ExposureFlowRoute,
> {
  name: Route;
  params?: ExposureStackParamList[Route];
}

export interface ExposureDetail extends LifestyleExposure {
  exposure: ExposureType | null;
  frequency?: ExposureFrequency | null;
  context?: string | null;
  other?: string | null;
}

export interface ExposureState {
  hasExposures?: boolean | null;
  lifestyleId?: string;
  selectedExposures: ExposureType[];
  exposureDetails: ExposureDetail[];
  exposureIdsToDelete: string[];
  navigationStack: ExposureNavigationEntry[];
  currentNavigationIndex: number;
}

const initialState: ExposureState = {
  hasExposures: null,
  lifestyleId: undefined,
  selectedExposures: [],
  exposureDetails: [],
  exposureIdsToDelete: [],
  navigationStack: [{ name: "ExposureTypes" }],
  currentNavigationIndex: 0,
};

interface ExposureContextValue {
  state: ExposureState;
  updateState: (updates: Partial<ExposureState>) => void;
  resetState: () => void;
  isSubmitting: boolean;
  submit: (state: ExposureState) => Promise<void>;
}

const ExposureContext = createContext<ExposureContextValue | null>(null);

export function ExposureProvider({ children }: { children: React.ReactNode }) {
  const { data: rawPatient } = useSuspensePatient();
  const patient = withMedicalHistoryRecordFixture(rawPatient);
  const { mutateAsync: updatePatient } = useQueuedPatientUpdate();

  const [state, setState] = useState<ExposureState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!patient) return;
    setState((prev) => {
      return getExposureState(patient, prev);
    });
  }, [patient]);

  const updateState = useCallback((updates: Partial<ExposureState>) => {
    setState((prev) => {
      const newState = { ...prev, ...updates };
      const stack = buildExposureNavigationStack(newState);
      return {
        ...newState,
        navigationStack: stack,
        currentNavigationIndex: Math.min(
          newState.currentNavigationIndex,
          Math.max(stack.length - 1, 0),
        ),
      };
    });
  }, []);

  const resetState = useCallback(
    (state?: ExposureState) => setState(state ?? initialState),
    [],
  );

  const submit = useCallback(
    async (state: ExposureState) => {
      const payload = transformExposureStateToRequest(state);
      if (Object.keys(payload).length === 0) {
        return;
      }
      setIsSubmitting(true);
      try {
        await updatePatient(payload);
      } finally {
        setIsSubmitting(false);
      }
    },
    [updatePatient],
  );

  return (
    <ExposureContext.Provider
      value={{
        state,
        updateState,
        resetState,
        isSubmitting,
        submit,
      }}
    >
      {children}
    </ExposureContext.Provider>
  );
}

export function useExposure(currentScreen: ExposureNavigationEntry) {
  const ctx = useContext(ExposureContext);
  if (!ctx) {
    throw new Error("useExposure must be used within ExposureProvider");
  }
  const navigation =
    useNavigation<NativeStackNavigationProp<ExposureStackParamList>>();

  const {
    state: fullState,
    updateState,
    resetState,
    isSubmitting,
    submit,
  } = ctx;
  const { navigationStack } = fullState;

  const next = useCallback(async () => {
    const stack = navigationStack?.length
      ? navigationStack
      : buildExposureNavigationStack(fullState);

    const currentIndex = stack.findIndex((entry) => {
      if (entry.name !== currentScreen.name) {
        return false;
      }
      // For screens without params, name match is enough
      if (!currentScreen.params && !entry.params) {
        return true;
      }
      // Match by params
      return (
        JSON.stringify(entry.params) === JSON.stringify(currentScreen.params)
      );
    });
    if (currentIndex < 0) {
      return;
    }

    const nextIndex = currentIndex + 1;
    const nextEntry = stack[nextIndex];

    if (nextEntry) {
      updateState({
        navigationStack: stack,
        currentNavigationIndex: nextIndex,
      });

      // navigate()'s overloads can't correlate a union-typed (name, params)
      // pair from ExposureNavigationEntry; the action object form stays
      // fully typed by the entry instead of needing casts.
      navigation.dispatch(
        CommonActions.navigate({
          name: nextEntry.name,
          params: nextEntry.params,
        }),
      );
    } else {
      // Reached end of flow, navigate to complete screen
      navigation.navigate("ExposureComplete");
    }
  }, [currentScreen, navigationStack, fullState, updateState, navigation]);

  return {
    state: fullState,
    updateState,
    resetState,
    next,
    isSubmitting,
    submit,
    isNextDisabled: isSubmitting,
  } as const;
}

function getExposureState(
  patient: Patient | undefined,
  previousState: ExposureState,
): ExposureState {
  if (!patient) return previousState;

  const exposures = patient?.lifestyle?.exposures ?? [];

  // Check if state is in initial empty state
  const isInitialState =
    previousState.hasExposures === null &&
    previousState.selectedExposures.length === 0 &&
    previousState.exposureDetails.length === 0;

  const next: Partial<ExposureState> = {};

  if (patient?.lifestyle?.id) {
    next.lifestyleId = patient.lifestyle.id;
  }

  // Load hasExposures (only if empty or initial state)
  if (
    (isInitialState || previousState.hasExposures === null) &&
    patient?.lifestyle?.hasExposures != null
  ) {
    next.hasExposures = patient.lifestyle.hasExposures;
  }

  // Load selectedExposures (only if empty or initial state)
  if (
    (isInitialState || previousState.selectedExposures.length === 0) &&
    exposures.length > 0
  ) {
    next.selectedExposures = Array.from(
      new Set(
        exposures
          .map((item) => item?.exposure)
          .filter((exposure): exposure is ExposureType => Boolean(exposure)),
      ),
    );
  }

  // Load exposureDetails (only if empty or initial state)
  if (
    (isInitialState || previousState.exposureDetails.length === 0) &&
    exposures.length > 0
  ) {
    next.exposureDetails = exposures.map((item) => ({
      ...item,
      id: item?.id ?? undefined,
      exposure: item?.exposure ?? null,
      frequency: item?.frequency ?? null,
      context: item?.context ?? null,
      other: item?.other ?? null,
    }));
  }

  // Only update if there are changes
  if (Object.keys(next).length === 0) {
    return previousState;
  }

  const mergedState = {
    ...previousState,
    ...next,
  };

  const stack = buildExposureNavigationStack(mergedState);

  return {
    ...mergedState,
    navigationStack: stack,
    currentNavigationIndex: Math.min(
      previousState.currentNavigationIndex ?? 0,
      Math.max(stack.length - 1, 0),
    ),
  };
}
